import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contractSettingsEndpoints } from '@/lib/api/endpoints';
import { contractSettingsKeys } from '@/lib/queryKeys';
import { apiGet, apiPatch, operationsClient } from '@/lib/api/client';

/**
 * Recurring Service & Contracts v1.1 (spec §14) — settings panel hooks.
 * Mirrors the pattern in `useReportSettings.ts`.
 */

export type ContractSettingsTab = 'defaults' | 'renewal' | 'cancellation' | 'qbo' | 'reviews';

export const CONTRACT_SETTINGS_TABS: ReadonlyArray<ContractSettingsTab> = [
  'defaults',
  'renewal',
  'cancellation',
  'qbo',
  'reviews',
];

/**
 * `enabled` lets a screen that is reachable without `contracts:view` skip a call
 * it knows the API will refuse — Settings > Reviews admits reminder-policy
 * viewers, who never see the request-rules tab.
 */
export function useContractSettings(options?: { enabled?: boolean }) {
  return useApiQuery({
    queryKey: contractSettingsKeys.all(),
    queryFn: async () => apiGet(operationsClient, contractSettingsEndpoints.all()),
    showErrorToast: false,
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateContractSettings() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async ({
      tab,
      patch,
    }: {
      tab: ContractSettingsTab;
      patch: Record<string, unknown>;
    }) => apiPatch(operationsClient, contractSettingsEndpoints.tab(tab), patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractSettingsKeys.all() });
    },
  });
}
